import { readAnalyticsConsent } from "./analytics-consent";
import {
  parseCampaignAttribution,
  parseStoredCampaignAttribution,
  type CampaignAttribution,
} from "./campaign-attribution";

const STORAGE_KEY = "peakwave_campaign_attribution";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

function campaignConsentAccepted(): boolean {
  try { return readAnalyticsConsent() === "accepted"; } catch { return false; }
}

export function clearCampaignAttribution(): void {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* Storage is optional. */ }
}

/** Retain the latest explicit campaign in this tab only, after consent. */
export function captureCampaignAttribution(search: string): void {
  if (!campaignConsentAccepted()) {
    clearCampaignAttribution();
    return;
  }
  const attribution = parseCampaignAttribution(Object.fromEntries(new URLSearchParams(search)));
  if (!attribution) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ attribution, capturedAt: Date.now() }));
  } catch { /* Signup still works when storage is unavailable. */ }
}

/** Read at the action boundary so withdrawn consent never uses cached state. */
export function getSignupCampaignAttribution(
  explicit?: CampaignAttribution | null,
): CampaignAttribution | null {
  if (!campaignConsentAccepted()) {
    clearCampaignAttribution();
    return null;
  }
  const current = parseStoredCampaignAttribution(explicit);
  if (current) return current;
  try {
    const raw: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? "null");
    if (!raw || typeof raw !== "object" || !("capturedAt" in raw) || !("attribution" in raw)) return null;
    const age = typeof raw.capturedAt === "number" ? Date.now() - raw.capturedAt : NaN;
    if (!Number.isFinite(age) || age < 0 || age > MAX_AGE_MS) {
      clearCampaignAttribution();
      return null;
    }
    return parseStoredCampaignAttribution(raw.attribution);
  } catch { return null; }
}
